import { useAuthStore } from '@/stores/auth'
import { useSocketStore } from '@/stores/socket'
import { useGlobalStore } from '@/stores/global'
import config from '@/helpers/config'
import remove from 'lodash/remove'
import SockJS from 'sockjs-client'
import { Client } from '@stomp/stompjs'
import { socketMessageTypeEnum } from '@/helpers/Constants'

const socketHandler = {
    connect: connect,
    disconnect: disconnect,
    addListener: addListener,
    removeListener: removeListener,
    deliver: deliver
}

function connect() {
    const authStore = useAuthStore()
    const socketStore = useSocketStore()
    const globalStore = useGlobalStore()
    const stompClient = new Client({
        webSocketFactory: function () {
            return new SockJS(`${config.SERVER_URL}:${config.SERVER_PORT}/${config.APP_PATH}/api/websocket-endpoint`)
        },
        reconnectDelay: 5000,
        onConnect: () => {
            socketStore.resetRetriesCounter()
            globalStore.toggleOff()
            stompClient.publish(
                {
                    destination: '/app/authenticate',
                    body: authStore.user.token
                }
            )
            stompClient.subscribe(
                '/user/notifications',
                message => deliver(message)
            )
        },
        onDisconnect: () => {
            socketStore.reset()
        },
        onWebSocketError: () => {
            retryConnection()
        },
        onWebSocketClose: (e) => {
            if (e.code !== 1000) {
                retryConnection()
            }
        },
    })
    socketStore.stompClient = stompClient
    stompClient.activate()
}

function disconnect() {
    const socketStore = useSocketStore()
    socketStore.stompClient.deactivate()
}

function retryConnection() {
    const socketStore = useSocketStore()
    const authStore = useAuthStore()
    const globalStore = useGlobalStore()
    if (socketStore.connectionRetriesLeft > 0) {
        globalStore.toggleOn()
        socketStore.connectionRetriesLeft--
    } else {
        socketStore.stompClient.deactivate()
        globalStore.toggleOff()
        authStore.logout()
    }
}

function addListener(socketMessageType, handler) {
    const socketStore = useSocketStore()
    let listenersByMessageType = socketStore.listeners.get(socketMessageType)
    if (listenersByMessageType === undefined) {
        listenersByMessageType = []
        socketStore.listeners.set(socketMessageType, listenersByMessageType)
    }
    listenersByMessageType.push(handler)
}

function removeListener(listenerId) {
    const socketStore = useSocketStore()
    socketStore.listeners.forEach(function (listenersByMessageType) {
        remove(listenersByMessageType, l => l.id === listenerId)
    })
}

function deliver(message) {
    const authStore = useAuthStore()
    const socketStore = useSocketStore()
    const parsedMessage = JSON.parse(message.body)
    if (parsedMessage.type === socketMessageTypeEnum.UNREAD_NOTIFICATIONS_COUNT) {
        authStore.unreadChatsMessages = parsedMessage.payload.unreadMessagesNotificationsCount
        authStore.unreadOperationsMessages = parsedMessage.payload.unreadOperationsNotificationsCount
        socketStore.listeners.get(socketMessageTypeEnum.UNREAD_NOTIFICATIONS_COUNT).forEach(l => {
            l.handler(parsedMessage.payload)
        })
        return
    }
    let listenersByMessageType
    // small trick: on server only CHAT_MESSAGE is available    
    if (parsedMessage.type === socketMessageTypeEnum.CHAT_MESSAGE && (parsedMessage.payload.operationId === undefined
        || parsedMessage.payload.operationId === null)) {
        listenersByMessageType = socketStore.listeners.get(socketMessageTypeEnum.FREE_CHAT_MESSAGE)
    } else {
        listenersByMessageType = socketStore.listeners.get(parsedMessage.type)
    }
    if (listenersByMessageType === null || listenersByMessageType === undefined || listenersByMessageType.length === 0) {
        if (parsedMessage.type === socketMessageTypeEnum.OPERATION_UPDATE && authStore.amIAdmin()) {
            // admin can't have a permanent operation notification (except created)
            return
        }
        handleDeliveryFail(parsedMessage, socketStore)
    } else {
        listenersByMessageType.forEach(l => {
            l.handler(parsedMessage.payload)
            if (l.createNotification) {
                handleDeliveryFail(parsedMessage, socketStore)
            }
        })
    }
}

function handleDeliveryFail(parsedMessage, socketStore) {
    if (parsedMessage.type === socketMessageTypeEnum.CHAT_MESSAGE) {
        socketStore.stompClient.publish(
            {
                destination: '/app/messageDeliveryFailed',
                body: JSON.stringify(parsedMessage.payload)
            }
        )
    } else if (parsedMessage.type === socketMessageTypeEnum.OPERATION_UPDATE) {
        socketStore.stompClient.publish(
            {
                destination: '/app/operationUpdateDeliveryFailed',
                body: JSON.stringify(parsedMessage.payload)
            }
        )
    }
}

export default socketHandler